import { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { TrendTimeSeriesRow } from '../types';

type TrendChartProps = {
  data: TrendTimeSeriesRow[];
  allData: TrendTimeSeriesRow[];
  isDark?: boolean;
};

type MergedRow = {
  date: string;
  count: number;
  allCount: number;
  positiveTrend: number | null;
};

const formatDate = (dateStr: string, showYear = false) => {
  const d = new Date(dateStr + 'T00:00:00Z');
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  };
  if (showYear) opts.year = '2-digit';
  return d.toLocaleDateString('en-US', opts);
};

const spansMultipleYears = (rows: { date: string }[]): boolean => {
  if (rows.length < 2) return false;
  return rows[0].date.slice(0, 4) !== rows[rows.length - 1].date.slice(0, 4);
};

const linearRegression = (
  values: number[],
): { slope: number; intercept: number } => {
  const n = values.length;
  if (n < 2) return { slope: 0, intercept: values[0] ?? 0 };
  let sumX = 0,
    sumY = 0,
    sumXY = 0,
    sumXX = 0;
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += values[i];
    sumXY += i * values[i];
    sumXX += i * i;
  }
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return { slope: 0, intercept: sumY / n };
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
};

const mergeTimeSeries = (
  positiveData: TrendTimeSeriesRow[],
  allData: TrendTimeSeriesRow[],
): MergedRow[] => {
  if (allData.length === 0) {
    const counts = positiveData.map((r) => r.count);
    const { slope, intercept } = linearRegression(counts);
    return positiveData.map((row, i) => ({
      date: row.date,
      count: row.count,
      allCount: 0,
      positiveTrend: Math.max(0, intercept + slope * i),
    }));
  }

  const positiveByDate = new Map(positiveData.map((row) => [row.date, row]));
  const merged = allData.map((allRow) => {
    const positiveRow = positiveByDate.get(allRow.date);
    return {
      date: allRow.date,
      allCount: allRow.count,
      count: positiveRow?.count ?? 0,
      positiveTrend: null as number | null,
    };
  });

  const counts = merged.map((r) => r.count);
  const { slope, intercept } = linearRegression(counts);
  for (let i = 0; i < merged.length; i++) {
    merged[i].positiveTrend = Math.max(0, intercept + slope * i);
  }

  return merged;
};

export default function TrendChart({
  data,
  allData,
  isDark = false,
}: TrendChartProps) {
  const axisColor = isDark ? '#9ca3af' : '#6b7280';
  const gridColor = isDark ? '#374151' : '#e5e7eb';
  const allStroke = isDark ? '#9ca3af' : '#94a3b8';
  const chartData = useMemo(
    () => mergeTimeSeries(data, allData),
    [data, allData],
  );
  const showYear = useMemo(() => spansMultipleYears(chartData), [chartData]);

  if (chartData.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-neutral-400 dark:text-neutral-500">
        No time series data for this period
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart
        data={chartData}
        margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
      >
        <defs>
          <linearGradient id="positiveGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#ec4899" stopOpacity={0.02} />
          </linearGradient>
          <linearGradient id="allGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={allStroke} stopOpacity={0.25} />
            <stop offset="95%" stopColor={allStroke} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
        <XAxis
          dataKey="date"
          tickFormatter={(val) => formatDate(String(val), showYear)}
          tick={{ fill: axisColor, fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fill: axisColor, fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          width={36}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: isDark ? '#1f2937' : '#ffffff',
            borderColor: isDark ? '#374151' : '#e5e7eb',
            borderRadius: '8px',
            fontSize: '12px',
            color: isDark ? '#f9fafb' : '#111827',
          }}
          labelFormatter={(label) => formatDate(String(label), showYear)}
          formatter={(value, name) => {
            const v = Number(value);
            if (name === 'allCount')
              return [v.toLocaleString(), 'All Articles'];
            if (name === 'count')
              return [v.toLocaleString(), 'Positive Articles'];
            if (name === 'positiveTrend') return [v.toFixed(1), 'Trend'];
            return [v, String(name)];
          }}
        />
        <Legend
          wrapperStyle={{ fontSize: '12px', color: axisColor }}
          formatter={(value) => {
            if (value === 'allCount') return 'All Articles';
            if (value === 'count') return 'Positive Articles';
            if (value === 'positiveTrend') return 'Trend';
            return String(value);
          }}
        />
        <Area
          type="monotone"
          dataKey="allCount"
          stroke={allStroke}
          strokeWidth={1.5}
          fill="url(#allGradient)"
          dot={false}
          activeDot={{ r: 3, stroke: allStroke, strokeWidth: 2 }}
        />
        <Area
          type="monotone"
          dataKey="count"
          stroke="#ec4899"
          strokeWidth={2}
          fill="url(#positiveGradient)"
          dot={false}
          activeDot={{ r: 4, stroke: '#ec4899', strokeWidth: 2 }}
        />
        <Line
          type="linear"
          dataKey="positiveTrend"
          stroke="#db2777"
          strokeWidth={2}
          strokeDasharray="6 3"
          dot={false}
          activeDot={false}
          connectNulls
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
